import React, { useState } from "react";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { submitLead } from "@/lib/api";
import { useT } from "@/lib/i18n";

const ORIGINS = [
  "Afghanistan","Albania","Algeria","Argentina","Armenia","Australia","Austria","Azerbaijan",
  "Bahrain","Bangladesh","Belarus","Belgium","Bolivia","Bosnia and Herzegovina","Brazil","Bulgaria",
  "Cambodia","Canada","Chile","China","Colombia","Costa Rica","Croatia","Cyprus","Czech Republic",
  "Denmark","Dominican Republic","Ecuador","Egypt","Estonia","Ethiopia","Finland","France",
  "Georgia","Germany","Ghana","Greece","Guatemala","Honduras","Hong Kong","Hungary","Iceland",
  "India","Indonesia","Iraq","Ireland","Israel","Italy","Ivory Coast","Japan","Jordan",
  "Kazakhstan","Kenya","Kuwait","Kyrgyzstan","Laos","Latvia","Lebanon","Lithuania","Luxembourg",
  "Malaysia","Malta","Mexico","Moldova","Mongolia","Morocco","Myanmar","Nepal","Netherlands",
  "New Zealand","Nigeria","North Macedonia","Norway","Oman","Pakistan","Panama","Paraguay","Peru",
  "Philippines","Poland","Portugal","Qatar","Romania","Russia","Saudi Arabia","Senegal","Serbia",
  "Singapore","Slovakia","Slovenia","South Africa","South Korea","Spain","Sri Lanka","Sweden",
  "Switzerland","Taiwan","Tajikistan","Tanzania","Thailand","Tunisia","Turkey","Turkmenistan",
  "Uganda","Ukraine","United Arab Emirates","United Kingdom","United States","Uruguay",
  "Uzbekistan","Venezuela","Vietnam","Yemen","Zambia","Zimbabwe","Other",
];
const DESTINATIONS = [
  "Austria","Belgium","Bulgaria","Croatia","Cyprus","Czech Republic","Denmark","Estonia",
  "Finland","France","Germany","Greece","Hungary","Ireland","Italy","Latvia","Lithuania",
  "Luxembourg","Malta","Netherlands","Norway","Poland","Portugal","Romania","Slovakia",
  "Slovenia","Spain","Sweden","Switzerland","United Kingdom","Albania","Bosnia and Herzegovina",
  "Iceland","Liechtenstein","Moldova","Montenegro","North Macedonia","Serbia","Ukraine",
  "Other EU","Other Non-EU",
];
const CARGO_TYPES = [
  "General Cargo", "Amazon FBA", "Exhibition Cargo", "Pharma / Healthcare",
  "E-Commerce Parcels", "Automotive (White-Glove)", "Aerospace (White-Glove)",
  "Turkey Lane (LTL)", "Industrial / Project", "Hazardous (DG)",
];

const initial = {
  name: "",
  company: "",
  email: "",
  phone: "",
  origin_country: "",
  destination: "",
  cargo_type: "",
  shipment_details: "",
};

export default function LeadForm({ source = "website", title, compact = false }) {
  const { t } = useT();
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error(t("form.errMin"));
      return;
    }
    setLoading(true);
    try {
      await submitLead({ ...form, source });
      setDone(true);
      toast.success(t("form.okToast"));
      setForm(initial);
    } catch (err) {
      console.error(err);
      toast.error(t("form.errSubmit"));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="lead-form-success" className="zp-card p-10 text-center">
        <div className="zp-label mb-3">Confirmed</div>
        <h3 className="font-display text-3xl tracking-tight mb-3">
          {t("form.successTitle")}
        </h3>
        <p className="text-[color:var(--zp-text-2)] max-w-md mx-auto">
          {t("form.successDesc")}
        </p>
        <button
          data-testid="lead-form-new"
          className="zp-btn-ghost mt-8"
          onClick={() => setDone(false)}
        >
          {t("form.newSubmission")}
        </button>
      </div>
    );
  }

  return (
    <form
      data-testid="lead-form"
      onSubmit={onSubmit}
      className={`zp-card ${compact ? "p-6 sm:p-8" : "p-8 sm:p-10"}`}
      noValidate
    >
      {title && (
        <div className="mb-7">
          <div className="zp-label mb-2">Request</div>
          <h3 className="font-display text-2xl sm:text-3xl tracking-tight">
            {title}
          </h3>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label={t("form.name")} testId="lead-name">
          <input required value={form.name} onChange={update("name")} className="zp-input" placeholder="Jane Doe" data-testid="lead-input-name" />
        </Field>
        <Field label={t("form.company")} testId="lead-company">
          <input value={form.company} onChange={update("company")} className="zp-input" placeholder="Acme Logistics Pvt Ltd" data-testid="lead-input-company" />
        </Field>
        <Field label={t("form.email")} testId="lead-email">
          <input type="email" required value={form.email} onChange={update("email")} className="zp-input" placeholder="you@company.com" data-testid="lead-input-email" />
        </Field>
        <Field label={t("form.phone")} testId="lead-phone">
          <input value={form.phone} onChange={update("phone")} className="zp-input" placeholder="+49 152 ..." data-testid="lead-input-phone" />
        </Field>
        <Field label={t("form.origin")} testId="lead-origin">
          <select value={form.origin_country} onChange={update("origin_country")} className="zp-input" data-testid="lead-input-origin">
            <option value="">{t("form.selectOrigin")}</option>
            {ORIGINS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
        <Field label={t("form.destination")} testId="lead-destination">
          <select value={form.destination} onChange={update("destination")} className="zp-input" data-testid="lead-input-destination">
            <option value="">{t("form.selectDest")}</option>
            {DESTINATIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
        <Field label={t("form.cargo")} testId="lead-cargo" className="md:col-span-2">
          <select value={form.cargo_type} onChange={update("cargo_type")} className="zp-input" data-testid="lead-input-cargo">
            <option value="">{t("form.selectCargo")}</option>
            {CARGO_TYPES.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>
        <Field label={t("form.details")} testId="lead-details" className="md:col-span-2">
          <textarea value={form.shipment_details} onChange={update("shipment_details")} className="zp-input" placeholder={t("form.detailsPh")} data-testid="lead-input-details" />
        </Field>
      </div>

      <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
        <button
          type="submit"
          disabled={loading}
          className="zp-btn-primary sm:w-auto w-full"
          data-testid="lead-submit-btn"
        >
          {loading ? (
            <>
              <Loader2 size={16} className="animate-spin" /> {t("common.sending")}
            </>
          ) : (
            <>
              {t("common.send")} <Send size={16} />
            </>
          )}
        </button>
        <p className="text-xs text-[color:var(--zp-text-3)] max-w-md">
          {t("form.disclaimer")}
        </p>
      </div>
    </form>
  );
}

function Field({ label, children, className = "", testId }) {
  return (
    <label data-testid={testId} className={`block ${className}`}>
      <span className="zp-label block mb-2">{label}</span>
      {children}
    </label>
  );
}
